import * as THREE from 'three';
import { COMMON_UNIFORMS, LIGHT_FNS, NOISE_FNS, SKY_FNS, WIND_FNS } from '../shaders/common';
import type { Environment } from './Environment';
import { Simplex2, lerp, smoothstep } from './noise';

export const TERRAIN_RES = 385;
export const TERRAIN_CELL = 4;
export const TERRAIN_HALF = ((TERRAIN_RES - 1) * TERRAIN_CELL) / 2; // 768 m
export const PLAYABLE_RADIUS = 520;
const CHUNK_CELLS = 64;
const CLIP_RADIUS = 712;
const RING_INNER = 700;
const RING_OUTER = 9000;

export const MAX_CLEARINGS = 8;
export const GRASS_TILE = 8;

/** A hill placed in the sun direction so the opening view frames the sun above a soft ridge. */
const SUN_HILL = new THREE.Vector2(55, -340);

export class Terrain {
  readonly heights = new Float32Array(TERRAIN_RES * TERRAIN_RES);
  readonly heightTexture: THREE.DataTexture;
  readonly group = new THREE.Group();
  readonly material: THREE.ShaderMaterial;
  readonly clearings: THREE.Vector4[] = [];
  /** Per 8 m grass tile min/max ground height, for tight frustum culling. */
  readonly tileMin: Float32Array;
  readonly tileMax: Float32Array;
  readonly tileCount: number;
  private readonly simplex: Simplex2;

  constructor(private readonly env: Environment, seed = 1337) {
    this.simplex = new Simplex2(seed);
    for (let i = 0; i < MAX_CLEARINGS; i++) this.clearings.push(new THREE.Vector4(0, 0, 0, 0));

    const spawnHeight = this.rawHeight(0, 0);
    for (let iz = 0; iz < TERRAIN_RES; iz++) {
      for (let ix = 0; ix < TERRAIN_RES; ix++) {
        const x = -TERRAIN_HALF + ix * TERRAIN_CELL;
        const z = -TERRAIN_HALF + iz * TERRAIN_CELL;
        let h = this.rawHeight(x, z);
        // Soften the spawn meadow into a gentle slope that faces the sun hill.
        const r = Math.hypot(x, z);
        const flat = smoothstep(80, 10, r) * 0.65;
        h = lerp(h, spawnHeight - z * 0.018, flat);
        this.heights[iz * TERRAIN_RES + ix] = h;
      }
    }

    this.heightTexture = new THREE.DataTexture(this.heights, TERRAIN_RES, TERRAIN_RES, THREE.RedFormat, THREE.FloatType);
    this.heightTexture.magFilter = THREE.NearestFilter;
    this.heightTexture.minFilter = THREE.NearestFilter;
    this.heightTexture.generateMipmaps = false;
    this.heightTexture.needsUpdate = true;

    const tilesPerSide = Math.ceil((TERRAIN_HALF * 2) / GRASS_TILE);
    this.tileCount = tilesPerSide;
    this.tileMin = new Float32Array(tilesPerSide * tilesPerSide);
    this.tileMax = new Float32Array(tilesPerSide * tilesPerSide);
    for (let tz = 0; tz < tilesPerSide; tz++) {
      for (let tx = 0; tx < tilesPerSide; tx++) {
        const x0 = -TERRAIN_HALF + tx * GRASS_TILE;
        const z0 = -TERRAIN_HALF + tz * GRASS_TILE;
        let lo = Infinity;
        let hi = -Infinity;
        for (let sz = 0; sz <= 2; sz++) {
          for (let sx = 0; sx <= 2; sx++) {
            const h = this.heightAt(x0 + sx * GRASS_TILE * 0.5, z0 + sz * GRASS_TILE * 0.5);
            lo = Math.min(lo, h);
            hi = Math.max(hi, h);
          }
        }
        this.tileMin[tz * tilesPerSide + tx] = lo - 0.3;
        this.tileMax[tz * tilesPerSide + tx] = hi + 0.3;
      }
    }

    this.material = this.createMaterial();
    this.buildChunks();
    this.buildFarRing();
  }

  private rawHeight(x: number, z: number): number {
    const s = this.simplex;
    let h = s.fbm(x * 0.0021, z * 0.0021, 4) * 15;
    h += s.fbm(x * 0.0085 + 31.7, z * 0.0085 - 12.9, 3) * 3.2;
    h += s.noise(x * 0.04 + 5.1, z * 0.04 - 2.3) * 0.3;
    const dx = x - SUN_HILL.x;
    const dz = z - SUN_HILL.y;
    h += 21 * Math.exp(-(dx * dx + dz * dz) / (2 * 125 * 125));
    const r = Math.hypot(x, z);
    h += smoothstep(430, 760, r) * (24 + s.fbm(x * 0.004, z * 0.004, 3) * 12);
    return h;
  }

  /** Bilinear height, identical to the GLSL terrainHeight(). */
  heightAt(x: number, z: number): number {
    const gx = (x + TERRAIN_HALF) / TERRAIN_CELL;
    const gz = (z + TERRAIN_HALF) / TERRAIN_CELL;
    const ix = Math.floor(gx);
    const iz = Math.floor(gz);
    const fx = gx - ix;
    const fz = gz - iz;
    const max = TERRAIN_RES - 1;
    const x0 = Math.min(Math.max(ix, 0), max);
    const x1 = Math.min(Math.max(ix + 1, 0), max);
    const z0 = Math.min(Math.max(iz, 0), max);
    const z1 = Math.min(Math.max(iz + 1, 0), max);
    const h = this.heights;
    const h00 = h[z0 * TERRAIN_RES + x0];
    const h10 = h[z0 * TERRAIN_RES + x1];
    const h01 = h[z1 * TERRAIN_RES + x0];
    const h11 = h[z1 * TERRAIN_RES + x1];
    return (h00 + (h10 - h00) * fx) * (1 - fz) + (h01 + (h11 - h01) * fx) * fz;
  }

  normalAt(x: number, z: number, target: THREE.Vector3): THREE.Vector3 {
    const e = 1.5;
    const hl = this.heightAt(x - e, z);
    const hr = this.heightAt(x + e, z);
    const hd = this.heightAt(x, z - e);
    const hu = this.heightAt(x, z + e);
    return target.set(hl - hr, 2 * e, hd - hu).normalize();
  }

  setClearing(index: number, x: number, z: number, radius: number): void {
    this.clearings[index].set(x, z, radius, 0);
  }

  private createMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        ...this.env.uniforms,
        uClearings: { value: this.clearings },
      },
      vertexShader: /* glsl */ `
        varying vec3 vWorldPos;
        varying vec3 vNormal;
        void main() {
          vec4 wp = modelMatrix * vec4(position, 1.0);
          vWorldPos = wp.xyz;
          vNormal = normalize(mat3(modelMatrix) * normal);
          gl_Position = projectionMatrix * viewMatrix * wp;
        }
      `,
      fragmentShader: /* glsl */ `
        ${COMMON_UNIFORMS}
        uniform vec4 uClearings[${MAX_CLEARINGS}];
        varying vec3 vWorldPos;
        varying vec3 vNormal;
        ${NOISE_FNS}
        ${WIND_FNS}
        ${SKY_FNS}
        ${LIGHT_FNS}
        void main() {
          vec3 N = normalize(vNormal);
          vec3 V = normalize(cameraPosition - vWorldPos);
          float dist = length(cameraPosition - vWorldPos);
          vec2 xz = vWorldPos.xz;
          float nMid = texture2D(uNoise, xz / 37.0).g;
          float nBig = texture2D(uNoise, xz / 211.0).a;
          float nFine = texture2D(uNoise, xz / 3.1).b;
          float nHue = texture2D(uNoise, xz / 17.0).b;

          vec3 golden = vec3(0.5, 0.3, 0.09);
          vec3 straw = vec3(0.66, 0.47, 0.2);
          vec3 olive = vec3(0.3, 0.25, 0.08);
          vec3 rust = vec3(0.45, 0.2, 0.07);
          vec3 canopy = mix(golden, straw, 0.2 + nMid * 0.45);
          canopy = mix(canopy, olive, smoothstep(0.6, 0.95, nBig) * 0.45);
          canopy = mix(canopy, rust, smoothstep(0.68, 1.0, nHue) * 0.4);
          // Fine streaks read as blade clumps once real blades thin out.
          canopy *= mix(0.72, 1.08, nFine);
          vec3 soil = mix(vec3(0.035, 0.025, 0.014), vec3(0.09, 0.062, 0.035), nFine);

          float canopyMix = smoothstep(5.0, 34.0, dist);

          float clearing = 0.0;
          for (int i = 0; i < ${MAX_CLEARINGS}; i++) {
            vec4 c = uClearings[i];
            if (c.z > 0.0) {
              float cd = distance(xz, c.xy) + (nFine - 0.5) * c.z * 0.3;
              clearing = max(clearing, smoothstep(c.z, c.z * 0.6, cd));
            }
          }
          vec3 dirt = mix(vec3(0.15, 0.115, 0.075), vec3(0.27, 0.21, 0.14), nFine);
          canopyMix *= 1.0 - clearing;

          vec3 albedo = mix(soil, canopy * 0.92, canopyMix);
          albedo = mix(albedo, dirt, clearing);
          // Far hills read cooler and darker through the haze.
          albedo = mix(albedo, vec3(0.13, 0.13, 0.1), smoothstep(750.0, 2400.0, dist));

          float gust = gustAt(xz);
          albedo *= 1.0 + gust * 0.24 * canopyMix;

          vec3 Nc = normalize(mix(N, vec3(0.0, 1.0, 0.0), 0.6 * canopyMix));
          float shadow = cloudShadowAt(vWorldPos) * characterShadow(vWorldPos);
          float ao = mix(mix(0.35, 1.0, clearing), 1.0, canopyMix);
          float directOcc = mix(mix(0.45, 1.0, clearing), 1.0, canopyMix);
          float trans = 0.85 * canopyMix * mix(1.0, 0.55, smoothstep(150.0, 600.0, dist));
          vec3 col = foliageLight(albedo, Nc, V, ao, trans, shadow * directOcc, 0.05 * canopyMix + 0.02 * gust);
          col = applyAtmosphere(col, vWorldPos);
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
  }

  private buildChunks(): void {
    const chunks = (TERRAIN_RES - 1) / CHUNK_CELLS;
    const normal = new THREE.Vector3();
    for (let cz = 0; cz < chunks; cz++) {
      for (let cx = 0; cx < chunks; cx++) {
        const verts = CHUNK_CELLS + 1;
        const positions = new Float32Array(verts * verts * 3);
        const normals = new Float32Array(verts * verts * 3);
        for (let vz = 0; vz < verts; vz++) {
          for (let vx = 0; vx < verts; vx++) {
            const ix = cx * CHUNK_CELLS + vx;
            const iz = cz * CHUNK_CELLS + vz;
            const x = -TERRAIN_HALF + ix * TERRAIN_CELL;
            const z = -TERRAIN_HALF + iz * TERRAIN_CELL;
            const i = (vz * verts + vx) * 3;
            positions[i] = x;
            positions[i + 1] = this.heights[iz * TERRAIN_RES + ix];
            positions[i + 2] = z;
            this.normalAt(x, z, normal);
            normals[i] = normal.x;
            normals[i + 1] = normal.y;
            normals[i + 2] = normal.z;
          }
        }
        const indices: number[] = [];
        for (let vz = 0; vz < CHUNK_CELLS; vz++) {
          for (let vx = 0; vx < CHUNK_CELLS; vx++) {
            const a = vz * verts + vx;
            const b = a + 1;
            const c = a + verts;
            const d = c + 1;
            const px = positions[a * 3] + TERRAIN_CELL * 0.5;
            const pz = positions[a * 3 + 2] + TERRAIN_CELL * 0.5;
            if (Math.hypot(px, pz) > CLIP_RADIUS) continue;
            indices.push(a, c, b, b, c, d);
          }
        }
        if (indices.length === 0) continue;
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        geometry.setIndex(indices);
        geometry.computeBoundingSphere();
        geometry.computeBoundingBox();
        const mesh = new THREE.Mesh(geometry, this.material);
        mesh.matrixAutoUpdate = false;
        mesh.name = `terrain-${cx}-${cz}`;
        this.group.add(mesh);
      }
    }
  }

  private buildFarRing(): void {
    const radial = 320;
    const rings = 44;
    const positions: number[] = [];
    const s = this.simplex;
    const sunAngle = Math.atan2(this.env.sunDirection.x, -this.env.sunDirection.z);
    for (let ri = 0; ri <= rings; ri++) {
      const f = ri / rings;
      const r = RING_INNER * Math.pow(RING_OUTER / RING_INNER, f);
      for (let ai = 0; ai < radial; ai++) {
        const ang = (ai / radial) * Math.PI * 2;
        const x = Math.sin(ang) * r;
        const z = -Math.cos(ang) * r;
        const rimR = Math.min(r, 740);
        const rim = this.heightAt(Math.sin(ang) * rimR, -Math.cos(ang) * rimR);
        let angDiff = Math.abs(((ang - sunAngle + Math.PI * 3) % (Math.PI * 2)) - Math.PI);
        angDiff = smoothstep(0.15, 0.8, angDiff);
        const n = s.fbm(x * 0.00042 + 3.3, z * 0.00042 - 8.1, 5);
        const ridge = 1 - Math.abs(n);
        const amp = (smoothstep(900, 3800, r) * 190 + 20) * (0.3 + 0.7 * angDiff);
        const mountain = rim * 0.6 + ridge * ridge * amp + s.fbm(x * 0.002, z * 0.002, 3) * 14;
        const h = lerp(rim, mountain, smoothstep(RING_INNER, 1500, r));
        positions.push(x, ri === 0 ? rim - 2 : h, z);
      }
    }
    const indices: number[] = [];
    for (let ri = 0; ri < rings; ri++) {
      for (let ai = 0; ai < radial; ai++) {
        const a = ri * radial + ai;
        const b = ri * radial + ((ai + 1) % radial);
        const c = a + radial;
        const d = b + radial;
        indices.push(a, b, c, b, d, c);
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    const mesh = new THREE.Mesh(geometry, this.material);
    mesh.name = 'terrain-far-ring';
    mesh.matrixAutoUpdate = false;
    mesh.frustumCulled = false;
    this.group.add(mesh);
  }

  tileBounds(x: number, z: number): { min: number; max: number } | null {
    const tx = Math.floor((x + TERRAIN_HALF) / GRASS_TILE);
    const tz = Math.floor((z + TERRAIN_HALF) / GRASS_TILE);
    if (tx < 0 || tz < 0 || tx >= this.tileCount || tz >= this.tileCount) return null;
    const i = tz * this.tileCount + tx;
    return { min: this.tileMin[i], max: this.tileMax[i] };
  }
}
